import { useEffect, useState } from 'react';
import { Sparkles, Loader, RefreshCw, AlertCircle } from 'lucide-react';
import {
  analyzeSession,
  type SessionAnalysis,
  type SessionAnalysisCategory,
  type SessionAnalysisQuality,
  type SessionAnalysisSeverity,
} from '../../services/api/sessions';
import { cn } from '../../utils/cn';
import { QUALITY_LABELS } from '../../utils/quality-labels';
import { scrollToTurn } from './scroll-to-turn';

interface Props {
  sessionId: string;
  timelineLength: number;
  cachedAnalysis?: SessionAnalysis | null;
}

const QUALITY_COLORS: Record<SessionAnalysisQuality, string> = {
  good: 'bg-green-100 text-green-800 border-green-200',
  degraded: 'bg-amber-100 text-amber-800 border-amber-200',
  poor: 'bg-red-100 text-red-800 border-red-200',
};

const SEVERITY_STYLES: Record<SessionAnalysisSeverity, string> = {
  low: 'bg-slate-100 text-slate-700 border-slate-200',
  med: 'bg-amber-100 text-amber-800 border-amber-200',
  high: 'bg-red-100 text-red-800 border-red-200',
};

const CATEGORY_LABELS: Record<SessionAnalysisCategory, string> = {
  routing: 'Routing',
  tool: 'Verktyg',
  content: 'Innehåll',
  latency: 'Latens',
  ux: 'UX',
};

export const SessionAnalystPanel = ({ sessionId, timelineLength, cachedAnalysis }: Props) => {
  const [analysis, setAnalysis] = useState<SessionAnalysis | null>(cachedAnalysis ?? null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [wasCached, setWasCached] = useState<boolean>(Boolean(cachedAnalysis));

  useEffect(() => {
    setAnalysis(cachedAnalysis ?? null);
    setWasCached(Boolean(cachedAnalysis));
    setError(null);
  }, [sessionId, cachedAnalysis]);

  const run = async (refresh: boolean) => {
    setLoading(true);
    setError(null);
    try {
      const res = await analyzeSession(sessionId, refresh);
      setAnalysis(res.analysis);
      setWasCached(res.cached);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Kunde inte köra analys');
    } finally {
      setLoading(false);
    }
  };

  const staleCache = analysis && analysis.timelineLength !== timelineLength;

  return (
    <div className="rounded-xl border border-indigo-200 bg-gradient-to-br from-indigo-50/80 to-white p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-indigo-600" />
          <h4 className="text-sm font-semibold text-slate-900">AI Session Analyst</h4>
          {wasCached && analysis && !staleCache && (
            <span className="rounded-full bg-slate-200 px-2 py-0.5 text-[10px] uppercase tracking-wide text-slate-600">
              Cache
            </span>
          )}
          {staleCache && (
            <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] uppercase tracking-wide text-amber-800">
              Inaktuell
            </span>
          )}
        </div>

        <div className="flex items-center gap-2">
          {analysis && (
            <button
              type="button"
              onClick={() => run(true)}
              disabled={loading}
              className="inline-flex items-center gap-1 rounded-lg border border-slate-300 bg-white px-2.5 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              <RefreshCw className={cn('h-3 w-3', loading && 'animate-spin')} />
              Kör om
            </button>
          )}
          {!analysis && (
            <button
              type="button"
              onClick={() => run(false)}
              disabled={loading}
              className="inline-flex items-center gap-1 rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:opacity-50"
            >
              {loading ? (
                <Loader className="h-3 w-3 animate-spin" />
              ) : (
                <Sparkles className="h-3 w-3" />
              )}
              Analysera sessionen
            </button>
          )}
        </div>
      </div>

      {loading && !analysis && (
        <div className="mt-3 flex items-center gap-2 text-xs text-slate-500">
          <Loader className="h-3 w-3 animate-spin" />
          Kör analys (5-10s)...
        </div>
      )}

      {error && (
        <div className="mt-3 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
          <AlertCircle className="h-3 w-3" />
          {error}
        </div>
      )}

      {analysis && (
        <div className="mt-3 space-y-3">
          <div className="flex items-center gap-2">
            <span
              className={cn(
                'rounded-full border px-2.5 py-0.5 text-xs font-semibold',
                QUALITY_COLORS[analysis.quality],
              )}
            >
              {QUALITY_LABELS[analysis.quality]}
            </span>
            <span className="text-[11px] text-slate-500">
              {analysis.model} &bull; {new Date(analysis.analyzedAt).toLocaleString('sv-SE')}
            </span>
          </div>

          <p className="text-sm leading-6 text-slate-700">{analysis.summary}</p>

          {analysis.issues.length > 0 && (
            <div>
              <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                Observerade problem
              </div>
              <ul className="space-y-1.5">
                {analysis.issues.map((issue, i) => (
                  <li key={i} className="rounded-lg border border-slate-200 bg-white/70 p-2.5">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span
                        className={cn(
                          'rounded border px-1.5 py-0 text-[10px] font-semibold uppercase tracking-wide',
                          SEVERITY_STYLES[issue.severity],
                        )}
                      >
                        {issue.severity}
                      </span>
                      <span className="rounded bg-slate-100 px-1.5 py-0 text-[10px] text-slate-600">
                        {CATEGORY_LABELS[issue.category]}
                      </span>
                      {typeof issue.turnSequence === 'number' && (
                        <button
                          type="button"
                          onClick={() => scrollToTurn({ sequence: issue.turnSequence! })}
                          className="rounded bg-indigo-100 px-1.5 py-0 text-[10px] font-medium text-indigo-700 hover:bg-indigo-200"
                        >
                          Turn #{issue.turnSequence}
                        </button>
                      )}
                    </div>
                    <div className="mt-1 text-xs leading-5 text-slate-700">{issue.description}</div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {analysis.recommendations.length > 0 && (
            <div>
              <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                Rekommendationer
              </div>
              <ul className="list-disc space-y-0.5 pl-5 text-xs leading-5 text-slate-700">
                {analysis.recommendations.map((rec, i) => (
                  <li key={i}>{rec}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
